import { Fragment, useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { Button, Card, Input, Spinner } from "../components/ui";
import { fmtDateTime } from "../lib/format";

/**
 * ExactOnline items whose pack/piece unit no longer matches PBS's current pick size (see engine.ts's
 * runReconcileExact — the guard that skips a price write rather than post a price in the wrong unit).
 * ExactOnline cannot change an item's unit after creation, so the repair frees the code and rebuilds
 * the item under it (server/domain/unitFix.ts). "Repareren" plans exactly that as a change set; nothing
 * reaches ExactOnline until the set is approved and applied.
 */
export default function UnitMismatches() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null); // itemCode whose plan is expanded
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [, navigate] = useLocation();
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);

  const q = trpc.reconcile.unitMismatches.list.useQuery({ search: search || undefined }, { refetchInterval: 30000 });
  const items = q.data?.items ?? [];
  const plan = trpc.reconcile.unitMismatches.plan.useQuery({ itemCode: open ?? "" }, { enabled: !!open });
  const fix = trpc.reconcile.unitMismatches.fix.useMutation({
    onSuccess: (r) => {
      setSelected(new Set());
      if (r.skipped.length) setMsg({ text: `Change set #${r.changeSetId} klaar met ${r.planned} reparatie(s). Overgeslagen: ${r.skipped.map((s) => `${s.itemCode} (${s.reason})`).join("; ")}`, ok: r.planned > 0 });
      else navigate(`/reconcile/${r.changeSetId}`);
    },
    onError: (e) => setMsg({ text: e.message, ok: false }),
  });

  const toggle = (code: string) => setSelected((prev) => { const next = new Set(prev); next.has(code) ? next.delete(code) : next.add(code); return next; });
  const fixable = items.filter((m) => m.itemCode);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Unit mismatches</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Deze ExactOnline-artikelen zijn geprijsd in een andere pak/stuk-eenheid dan de huidige pick size van PBS verwacht — meestal omdat de verpakkingsgrootte ooit is gewijzigd. ExactOnline laat de eenheid van een bestaand artikel niet wijzigen, dus prijzen synchroniseren wordt hier overgeslagen (anders zou er een prijs voor de verkeerde eenheid worden weggeschreven). <strong>Repareren</strong> plant de oplossing: de oude code krijgt <code>#2</code>, het artikel wordt opnieuw aangemaakt op de vrijgekomen code met de juiste eenheid, en krijgt de actuele in- en verkoopprijs van PBS. Je ziet de stappen in een change set voordat er iets wordt verstuurd.
          </p>
        </div>
        <Input placeholder="Zoek op itemcode, PBS-code of naam…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </div>

      {msg && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${msg.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-3 text-xs underline">sluiten</button>
        </div>
      )}

      <Card title={`${(q.data?.total ?? 0).toLocaleString()} artikel${(q.data?.total ?? 0) === 1 ? "" : "en"} met een verkeerde eenheid`}>
        {q.isLoading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">{search ? "Niets gevonden voor deze zoekterm." : "Op dit moment geen — of er is nog geen reconcile gedraaid die het kon vaststellen."}</p>
        ) : (
          <>
            {q.data?.createdAt && (
              <p className="mb-3 text-xs text-slate-400">Uit de ExactOnline-reconcile van {fmtDateTime(q.data.createdAt)}{q.data.changeSetId ? <> (<Link href={`/reconcile/${q.data.changeSetId}`} className="text-indigo-600 hover:underline">change set #{q.data.changeSetId}</Link>)</> : null} — draai die opnieuw na een reparatie om deze lijst te verversen.</p>
            )}
            {q.data?.truncated && (
              <p className="mb-3 text-xs text-amber-600">⚠ De eerste {items.length.toLocaleString()} van {(q.data.total ?? 0).toLocaleString()} — zoek gerichter om een specifieke te vinden.</p>
            )}

            {fixable.length > 0 && (
              <div className="mb-3 flex items-center gap-3">
                <Button
                  onClick={() => fix.mutate({ itemCodes: [...selected] })}
                  disabled={selected.size === 0 || fix.isPending}
                >
                  {fix.isPending ? "Bezig…" : `Repareer ${selected.size || ""} geselecteerde`}
                </Button>
                <button
                  className="text-xs text-slate-500 underline"
                  onClick={() => setSelected(selected.size === fixable.length ? new Set() : new Set(fixable.map((m) => m.itemCode)))}
                >
                  {selected.size === fixable.length ? "selectie wissen" : `alle ${fixable.length} selecteren`}
                </button>
                <span className="text-xs text-slate-400">Maakt één change set; er gaat pas iets naar ExactOnline als je die toepast.</span>
              </div>
            )}

            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-8 py-2"></th>
                  <th className="py-2">Artikel</th>
                  <th>Geprijsd in (ExactOnline)</th>
                  <th>Verwacht (PBS pick size)</th>
                  <th className="text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((m, i) => (
                  <Fragment key={`${m.itemId}-${i}`}>
                    <tr className="align-top hover:bg-slate-50">
                      <td className="py-3">
                        {m.itemCode && (
                          <input type="checkbox" checked={selected.has(m.itemCode)} onChange={() => toggle(m.itemCode)} className="mt-1" aria-label={`Selecteer ${m.itemCode}`} />
                        )}
                      </td>
                      <td className="py-3">
                        <div className="font-medium text-slate-800">{m.name || m.productCode}</div>
                        <div className="text-xs text-slate-400">item {m.itemCode || "—"} · PBS-code {m.productCode}</div>
                      </td>
                      <td className="py-3 text-slate-600">{m.units.join(", ")}</td>
                      <td className="py-3 text-slate-600">{m.pickUnit} <span className="text-slate-400">(pick size {m.pickSize})</span></td>
                      <td className="py-3">
                        <div className="flex justify-end gap-3 text-xs">
                          {m.itemCode && (
                            <button onClick={() => setOpen(open === m.itemCode ? null : m.itemCode)} className="font-medium text-indigo-600 hover:underline">
                              {open === m.itemCode ? "verberg plan" : "toon plan"}
                            </button>
                          )}
                          {m.exactUrl && <a href={m.exactUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline">In ExactOnline ↗</a>}
                          <Link href={`/products/${m.productCode}`} className="font-medium text-indigo-600 hover:underline">Product</Link>
                        </div>
                      </td>
                    </tr>
                    {open === m.itemCode && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={5} className="px-3 py-3">
                          {plan.isLoading ? (
                            <Spinner />
                          ) : plan.error ? (
                            <p className="text-sm text-amber-700">{plan.error.message}</p>
                          ) : plan.data ? (
                            <div className="space-y-2 text-sm">
                              <div className="text-slate-600">
                                <strong>{plan.data.itemCode}</strong> → oude artikel wordt <strong>{plan.data.retiredCode}</strong>, nieuw artikel in <strong>{plan.data.to}</strong> (nu {plan.data.from || "onbekend"}) · inkoop € {plan.data.purchasePrice.toFixed(2)} · verkoop € {plan.data.salesPrice.toFixed(2)}
                              </div>
                              <ol className="list-decimal space-y-1 pl-5 text-slate-600">
                                {plan.data.steps.map((s, k) => (
                                  <li key={k}><span className="text-slate-400">{s.method}</span> {s.label}</li>
                                ))}
                              </ol>
                              {plan.data.warnings.length > 0 && (
                                <ul className="list-disc pl-5 text-amber-700">
                                  {plan.data.warnings.map((w, k) => <li key={k}>{w}</li>)}
                                </ul>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>
    </div>
  );
}
